import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, RefreshCcw, Zap, ZapOff, RotateCw, Check, Camera } from "lucide-react";

const MAX_SIDE = 2000;

function useCamera(active) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [facing, setFacing] = useState("environment");
  const [torch, setTorch] = useState(null);
  const [error, setError] = useState("");
  const stop = useCallback(() => { streamRef.current?.getTracks().forEach((t) => t.stop()); streamRef.current = null; }, []);
  useEffect(() => {
    if (!active) return undefined;
    let cancelled = false;
    setError("");
    navigator.mediaDevices?.getUserMedia({ video: { facingMode: { ideal: facing }, width: { ideal: 1920 }, height: { ideal: 1080 } }, audio: false })
      .then((s) => {
        if (cancelled) { s.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = s;
        if (videoRef.current) videoRef.current.srcObject = s;
        const caps = s.getVideoTracks()[0]?.getCapabilities?.() || {};
        setTorch(caps.torch ? false : null);
      })
      .catch((e) => setError(e?.name === "NotAllowedError" ? "denied" : "unavailable"));
    if (!navigator.mediaDevices?.getUserMedia) setError("unavailable");
    return () => { cancelled = true; stop(); };
  }, [active, facing, stop]);
  const toggleTorch = async () => {
    const track = streamRef.current?.getVideoTracks()[0];
    if (!track || torch === null) return;
    await track.applyConstraints({ advanced: [{ torch: !torch }] }).then(() => setTorch(!torch)).catch(() => setTorch(null));
  };
  const flip = () => setFacing((f) => (f === "environment" ? "user" : "environment"));
  const snap = () => {
    const v = videoRef.current;
    if (!v?.videoWidth) return null;
    const c = document.createElement("canvas");
    c.width = v.videoWidth;
    c.height = v.videoHeight;
    c.getContext("2d").drawImage(v, 0, 0);
    return c;
  };
  return { videoRef, error, torch, toggleTorch, flip, snap, stop };
}

function rotate(canvas) {
  const c = document.createElement("canvas");
  c.width = canvas.height;
  c.height = canvas.width;
  const ctx = c.getContext("2d");
  ctx.translate(c.width / 2, c.height / 2);
  ctx.rotate(Math.PI / 2);
  ctx.drawImage(canvas, -canvas.width / 2, -canvas.height / 2);
  return c;
}

function toFile(canvas, crop) {
  const sx = crop.x * canvas.width, sy = crop.y * canvas.height, sw = crop.w * canvas.width, sh = crop.h * canvas.height;
  const scale = Math.min(1, MAX_SIDE / Math.max(sw, sh));
  const out = document.createElement("canvas");
  out.width = Math.round(sw * scale);
  out.height = Math.round(sh * scale);
  out.getContext("2d").drawImage(canvas, sx, sy, sw, sh, 0, 0, out.width, out.height);
  return new Promise((r) => out.toBlob((b) => r(new File([b], `photo-${Date.now()}.jpg`, { type: "image/jpeg" })), "image/jpeg", 0.85));
}

const FULL = { x: 0, y: 0, w: 1, h: 1 };
const CORNERS = [["tl", "left-0 top-0 -translate-x-1/2 -translate-y-1/2"], ["tr", "right-0 top-0 translate-x-1/2 -translate-y-1/2"],
  ["bl", "left-0 bottom-0 -translate-x-1/2 translate-y-1/2"], ["br", "right-0 bottom-0 translate-x-1/2 translate-y-1/2"]];

function moveCorner(crop, corner, px, py) {
  const min = 0.08;
  let { x, y, w, h } = crop;
  const r = x + w, b = y + h;
  if (corner.includes("l")) { x = Math.min(Math.max(0, px), r - min); w = r - x; }
  if (corner.includes("r")) { w = Math.max(min, Math.min(1, px) - x); }
  if (corner.includes("t")) { y = Math.min(Math.max(0, py), b - min); h = b - y; }
  if (corner.includes("b")) { h = Math.max(min, Math.min(1, py) - y); }
  return { x, y, w, h };
}

function CropView({ canvas, crop, setCrop }) {
  const box = useRef(null);
  const src = useMemo(() => canvas.toDataURL("image/jpeg", 0.8), [canvas]);
  const drag = (corner) => (e) => {
    e.preventDefault();
    const move = (ev) => {
      const r = box.current.getBoundingClientRect();
      setCrop((c) => moveCorner(c, corner, (ev.clientX - r.left) / r.width, (ev.clientY - r.top) / r.height));
    };
    const up = () => { window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };
  return (
    <div ref={box} className="relative max-h-full max-w-full touch-none select-none" style={{ aspectRatio: `${canvas.width} / ${canvas.height}`, height: "100%" }}>
      <img src={src} alt="Captured page" className="h-full w-full object-contain" data-testid="camera-review-image" />
      <div className="absolute border-2 border-sky-400 shadow-[0_0_0_9999px_rgba(0,0,0,0.55)]"
        style={{ left: `${crop.x * 100}%`, top: `${crop.y * 100}%`, width: `${crop.w * 100}%`, height: `${crop.h * 100}%` }} data-testid="camera-crop-box">
        {CORNERS.map(([k, pos]) => (
          <span key={k} onPointerDown={drag(k)} data-testid={`camera-crop-${k}`} className={`absolute h-7 w-7 rounded-full bg-sky-400 border-4 border-black/40 cursor-grab ${pos}`} />
        ))}
      </div>
    </div>
  );
}

const ROUND = "h-12 w-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors";

function Blocked({ error, onClose, onPicker }) {
  return (
    <div className="flex-1 flex flex-col items-center justify-center text-center px-8 gap-4" data-testid="camera-error">
      <Camera className="h-8 w-8 text-sky-300" />
      <p className="text-sm text-neutral-200 max-w-sm">
        {error === "denied" ? "Camera access is blocked. Allow the camera for this site in your browser settings (the lock or camera icon next to the address), then try again." : "This browser can't open the camera here."}
      </p>
      <button onClick={onPicker} className="text-sm px-4 py-2 rounded-full bg-sky-400 text-white hover:bg-sky-500" data-testid="camera-use-picker-btn">Use your phone's camera app instead</button>
      <button onClick={onClose} className="text-xs text-neutral-400 hover:text-white">Close</button>
    </div>
  );
}

export function CameraCapture({ open, onClose, onCapture }) {
  const [shot, setShot] = useState(null);
  const [crop, setCrop] = useState(FULL);
  const cam = useCamera(open && !shot);
  const pickerRef = useRef(null);
  useEffect(() => { if (!open) { setShot(null); setCrop(FULL); } }, [open]);
  if (!open) return null;
  const take = () => { const c = cam.snap(); if (c) { setShot(c); setCrop(FULL); } };
  const use = async () => { onCapture(await toFile(shot, crop)); setShot(null); onClose(); };
  const close = () => { cam.stop(); onClose(); };
  return createPortal(
    <div className="fixed inset-0 z-[60] bg-black flex flex-col rise" data-testid="camera-overlay">
      <input ref={pickerRef} type="file" accept="image/*" capture="environment" hidden data-testid="camera-native-input"
        onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) { onCapture(f); onClose(); } }} />
      <div className="flex justify-between items-center p-4">
        <button onClick={close} className={ROUND} data-testid="camera-close-btn" aria-label="Close camera"><X className="h-5 w-5" /></button>
        {!shot && cam.torch !== null && (
          <button onClick={cam.toggleTorch} className={ROUND} data-testid="camera-torch-btn" aria-label="Flash">{cam.torch ? <Zap className="h-5 w-5 text-yellow-300" /> : <ZapOff className="h-5 w-5" />}</button>
        )}
      </div>
      {cam.error && !shot ? <Blocked error={cam.error} onClose={close} onPicker={() => pickerRef.current?.click()} /> : (
        <div className="flex-1 min-h-0 flex items-center justify-center px-4">
          {shot ? <CropView canvas={shot} crop={crop} setCrop={setCrop} />
            : <video ref={cam.videoRef} autoPlay playsInline muted className="max-h-full max-w-full rounded-2xl" data-testid="camera-preview" />}
        </div>
      )}
      <div className="p-6 pb-[max(1.5rem,env(safe-area-inset-bottom))] flex items-center justify-center gap-8">
        {shot ? (
          <>
            <button onClick={() => setShot(null)} className="text-sm px-4 py-2.5 rounded-full border border-white/15 text-white hover:bg-white/10" data-testid="camera-retake-btn">Retake</button>
            <button onClick={() => { setShot(rotate(shot)); setCrop(FULL); }} className={ROUND} data-testid="camera-rotate-btn" aria-label="Rotate"><RotateCw className="h-5 w-5" /></button>
            <button onClick={use} className="text-sm px-5 py-2.5 rounded-full bg-sky-400 text-white hover:bg-sky-500 inline-flex items-center gap-1.5" data-testid="camera-use-btn"><Check className="h-4 w-4" /> Use photo</button>
          </>
        ) : !cam.error && (
          <>
            <span className="w-12" />
            <button onClick={take} className="h-20 w-20 rounded-full border-4 border-white bg-white/20 hover:bg-white/40 transition-colors" data-testid="camera-shutter-btn" aria-label="Take photo" />
            <button onClick={cam.flip} className={ROUND} data-testid="camera-flip-btn" aria-label="Switch camera"><RefreshCcw className="h-5 w-5" /></button>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}
